<template>
	<div class="cars-page">
		<CarsFiltersModalMobile />
		<div class="cars-page__container">
			<Transition name="fade">
				<div v-if="locationsBar && bookingStore.getBookerLocations?.start?.crmID" class="cars-page__bar">
					<CarsLocationReservationsBar :location="bookingStore.getBookerLocations.start" @close="locationsBar = false" />
				</div>
			</Transition>
			<div class="cars-page__filters">
				<div class="cars-page__choose-filters">
					<CarsFilters />
				</div>
			</div>
			<div class="cars-page__main">
				<div class="cars-page__main-info">
					<span v-if="!carsStore.getCarsLoading">{{ carsStore.getFilteredCars?.length }}</span>
					<!-- <Skeletor :height="24" :width="28" v-else /> -->
					<v-progress-circular v-else size="26" indeterminate></v-progress-circular>
					<span> {{ $t('pages.cars.search_results') }}</span>
				</div>
				<Transition name="fade">
					<div v-if="carsStore.getCarsLoading" class="cars-page__cars-container">
						<Skeletor v-for="i in 14" :key="i" class="cars-page__cars-container-skeletor" round :height="552" />
					</div>
				</Transition>
				<Transition name="fade">
					<div v-if="!carsStore.getCarsLoading" class="cars-page__cars-container">
						<CarsSingleCar v-for="(car, i) in carsStore.getFilteredCars" :key="car.id" :car="car" :index="i" />
					</div>
				</Transition>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Skeletor } from 'vue-skeletor'
const cookie = useCookie('cf-car')
cookie.value = null
const isFiltersModalOpen = ref(false)
const step = inject<Ref<number>>('step')!
if (step) step.value = 0
const $t = useI18n().t
onMounted(() => {
	window.scrollTo(0, 0)
})
const carsStore = useCarsStore()
const locationsStore = useLocationsStore()
const bookingStore = useBookingStore()
// const reservationStore = useReservationStore()
const bookerStore = useBookerStore()
const locationsBar = ref(true)

// onMounted(() => {
// 	bookingStore.fetchBooking()
// })

const sendAnalyticsData = () => {
	const gtmService = useGTMService()
	const cars = carsStore.getFilteredCars ?? []
	if (!gtmService) return
	const prices = cars?.map((car) => car.segment.promotionalPrice?.amountGross ?? car.segment.regularPrice?.amountGross ?? car.segment.pricePerDay)
	const uniqueSegments = new Map()
	cars.forEach((car) => {
		const { id, name_en } = car.segment
		if (!uniqueSegments.has(id)) {
			uniqueSegments.set(id, name_en)
		}
	})
	const GTMParameters = {
		price_minimum: prices
			? Number((Math.min(...prices) * (useBookingStore().getDays ?? 1) * (Number(useCookie('currency-exchange-rate').value) ?? 1)).toFixed(2))
			: null,
		price_maximum: prices
			? Number((Math.max(...prices) * (useBookingStore().getDays ?? 1) * (Number(useCookie('currency-exchange-rate').value) ?? 1)).toFixed(2))
			: null,
		available_car_id: cars.map((car) => car.id),
		available_car_name: cars.map((car) => getCarNameLabel(car)),
		available_segments_id: Array.from(uniqueSegments.keys()),
		available_segments_name: Array.from(uniqueSegments.values()),
	}
	gtmService.searchListingViewed(GTMParameters)
}

watch(
	() => carsStore.getFilteredCars,
	() => {
		sendAnalyticsData()
	},
	{ deep: true },
)

// const fetchData = async () => {
// 	try {
// if (carsStore.getCurrentFilters?.length) return
// await Promise.all([
// ])
// 	} catch (err) {
// 		console.error(err)
// 	}
// }
if (process.server) {
	await carsStore.fetchAllCars()
} else if (process.client) {
	carsStore.fetchAllCars()
}

onMounted(() => {
	carsStore.fetchAllFilters()
	carsStore.fetchAllSegments()
	carsStore.fetchAllGroups()
	locationsStore.fetchLocations()
})

const fetchCarsWithFilters = async () => {
	try {
		await carsStore.fetchCarsWithFilters()
	} catch (err) {
		console.error(err)
	}
}
// const currentFilters = ref(carsStore.getCurrentFilters)
const config = useNuxtApp().$config
const route = useRoute()

watch(
	() => [carsStore.getCurrentFilters, carsStore.getCurrentGroups, carsStore.getCurrentPriceRange],
	() => {
		fetchCarsWithFilters()
	},
	{ deep: true },
)

watch(
	() => [bookerStore.getBookerDates, bookerStore.getBookerStartLocation],
	() => {
		fetchCarsWithFilters()
	},
	{ deep: true },
)

provide('isFiltersModalOpen', isFiltersModalOpen)

definePageMeta(pageMeta.RESERVATION.CARS)

const sendAnalyticsDataViewed = () => {
	const gtmService = useGTMService()
	if (!gtmService) return
	gtmService.pageViewed({ page_type: 'cars' })
}
if (process.client) sendAnalyticsDataViewed()

const metaRobots = computed(() => {
	if (config.public.environment === 'production-to-change') {
		return 'index, follow'
	} else {
		return 'noindex, nofollow'
	}
})
useHead({
	title: $t('pages.cars.meta_title') + ' | CarFree',
	link: [{ rel: 'canonical', href: config.public.baseURL + route.fullPath }],
	meta: [
		{ name: 'robots', content: metaRobots.value },
		{ name: 'description', content: $t('common.cars_description') },
		{ property: 'og:title', content: $t('pages.cars.meta_title') + ' | CarFree' },
		{ property: 'og:description', content: $t('common.cars_description') },
	],
})
</script>

<style lang="scss">
@import 'vue-skeletor/dist/vue-skeletor.css';
@import './cars';
</style>
